// src/components/MapWithDrawing/DrawingEditor.js
import React, { useState, useCallback, useEffect } from 'react';
import {
    Box, TextField, Typography, Popover, Button, Grid, FormControl, InputLabel, Select, MenuItem
} from '@material-ui/core';
import { SketchPicker } from 'react-color';
import { Save } from '@material-ui/icons';
import { defaultStyle } from './mapUtils'; // Import default style
import { AREA_TYPES, AREA_TYPE_LABELS, AREA_TYPE_COLORS } from './constants';

// Memoize this component
const DrawingEditor = React.memo(({
    editingShape,
    onEditChange,
    onStyleChange,
    onSave,
    onCancel,
    isDrawingMode,
    colorSwatchStyle // Pass style class via prop
}) => {
    console.log('editingShape...Drawing Editor', editingShape)
    console.log('Drawing Editor ...drawingMode', isDrawingMode)


    const [fillColorAnchorEl, setFillColorAnchorEl] = useState(null);
    const [strokeColorAnchorEl, setStrokeColorAnchorEl] = useState(null);

    // useEffect(() => {
    //     console.log('Editing Shape Changed!!!', editingShape)
    // }, [editingShape])

    const handleOpenFillColorPicker = useCallback((event) => setFillColorAnchorEl(event.currentTarget), []);
    const handleCloseFillColorPicker = useCallback(() => setFillColorAnchorEl(null), []);
    const handleOpenStrokeColorPicker = useCallback((event) => setStrokeColorAnchorEl(event.currentTarget), []);
    const handleCloseStrokeColorPicker = useCallback(() => setStrokeColorAnchorEl(null), []);

    const handleFillColorChangeComplete = useCallback((color) => {
        onStyleChange('fillColor', color.hex);
        onStyleChange('fillOpacity', 0.3);
    }, [onStyleChange]);

     const handleStrokeColorChangeComplete = useCallback((color) => {
        onStyleChange('color', color.hex); // 'color' is stroke color
        onStyleChange('opacity', color.rgb.a ?? 1);
    }, [onStyleChange]);
    let customStyles = {}
    if (editingShape?.fillColor) {
        customStyles.fillOpacity = 0.3
        customStyles.fillColor = editingShape.fillColor
    }
    if (editingShape?.strokeColor) customStyles.color = editingShape.strokeColor
    const newStyles = {
        ...customStyles,
        ...(editingShape?.style || {})
    }
    console.log('newStyles..set this.', newStyles)
    // Get current styles safely, falling back to defaults
    const currentStyle = { ...defaultStyle, ...newStyles };
    console.log('Current Style ', currentStyle)
    const showStyleControls = editingShape && editingShape.type !== 'marker';
    // console.log('Editing Shape??')
    if (!editingShape && !isDrawingMode) {
         return (
             <>
                <Typography variant="subtitle1" gutterBottom>Edit Selected Area</Typography>
                <Typography variant="body2" color="textSecondary" style={{ marginTop: '8px' }}>
                    Select a saved area from the list to view or edit its details.
                </Typography>
             </>
         );
    }
     if (!editingShape && isDrawingMode) {
         return (
             <>
                 <Typography variant="subtitle1" gutterBottom>Edit Selected Area</Typography>
                 <Typography variant="body2" color="textSecondary" style={{ marginTop: '8px' }}>
                     Drawing... Finish or cancel the current operation.
                 </Typography>
             </>
         );
     }

    // If we have an editingShape
    return (
        <>
            <Typography variant="subtitle1" gutterBottom>Edit Selected Area</Typography>
            <Box component="form" noValidate autoComplete="off" display="flex" flexDirection="column" gap={2}>
                <TextField
                    label="Area Name"
                    name="name" // Ensure name attribute matches state key
                    variant="outlined"
                    size="small"
                    value={editingShape.name || ''} // Handle potential undefined name
                    onChange={onEditChange} // Pass event directly
                    fullWidth
                    disabled={isDrawingMode}
                />

                <FormControl variant="outlined" size="small" fullWidth disabled={isDrawingMode}>
                    <InputLabel>Area Type</InputLabel>
                    <Select
                        label="Area Type"
                        name="areaType"
                        value={editingShape.areaType || AREA_TYPES.TURF}
                        onChange={(e) => {
                            onEditChange(e);
                            // Optionally update color based on area type
                            const newColor = AREA_TYPE_COLORS[e.target.value];
                            if (newColor) {
                                onStyleChange('fillColor', newColor);
                            }
                        }}
                    >
                        {Object.entries(AREA_TYPE_LABELS).map(([value, label]) => (
                            <MenuItem key={value} value={value}>
                                {label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {showStyleControls && (
                    <>
                        {/* Fill Color */}
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                            <Typography variant="body2">Fill Color & Opacity:</Typography>
                            <Box
                                className={colorSwatchStyle}
                                style={{ backgroundColor: currentStyle.fillColor, opacity: currentStyle.fillOpacity }}
                                onClick={handleOpenFillColorPicker}
                                title={`Fill: ${currentStyle.fillColor} @ ${(currentStyle.fillOpacity * 100).toFixed(0)}%`}
                            />
                        </Box>
                        <Popover
                            open={Boolean(fillColorAnchorEl)} anchorEl={fillColorAnchorEl} onClose={handleCloseFillColorPicker}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                        >
                            <SketchPicker color={currentStyle.fillColor} onChangeComplete={handleFillColorChangeComplete} disableAlpha={false} />
                        </Popover>

                        {/* Stroke Color */}
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                            <Typography variant="body2">Stroke Color & Opacity:</Typography>
                            <Box
                                className={colorSwatchStyle}
                                style={{ backgroundColor: currentStyle.color, opacity: currentStyle.opacity }}
                                onClick={handleOpenStrokeColorPicker}
                                title={`Stroke: ${currentStyle.color} @ ${(currentStyle.opacity * 100).toFixed(0)}%`}
                            />
                        </Box>
                        <Popover
                            open={Boolean(strokeColorAnchorEl)} anchorEl={strokeColorAnchorEl} onClose={handleCloseStrokeColorPicker}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                        >
                            <SketchPicker color={currentStyle.color} onChangeComplete={handleStrokeColorChangeComplete} disableAlpha={false} />
                        </Popover>

                        {/* Stroke Weight */}
                        <TextField
                            label="Stroke Weight (px)"
                            name="weight" // Name matches style key
                            type="number" variant="outlined" size="small"
                            value={currentStyle.weight}
                            // Handle direct style change for weight
                            onChange={(e) => onStyleChange('weight', Math.max(0, parseInt(e.target.value, 10) || 0))}
                            inputProps={{ min: 0, step: 1 }} fullWidth disabled={isDrawingMode}
                        />
                    </>
                )}
                {showStyleControls && editingShape && (
                    <Box mt={2} p={2} bgcolor="#f5f5f5" borderRadius={1} border="1px solid #e0e0e0">
                        <Typography variant="subtitle2" gutterBottom>Geometry Information</Typography>
                        <Grid container spacing={1}>
                            <Grid item xs={6}>
                                <Typography variant="body2">
                                    Area: {parseFloat(editingShape.area || 0).toFixed(2)} sq ft
                                </Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="body2">
                                    ({parseFloat(editingShape.areaAcres || 0).toFixed(4)} acres)
                                </Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="body2">
                                    Perimeter: {parseFloat(editingShape.perimeter || 0).toFixed(2)} lin ft
                                </Typography>
                            </Grid>
                        </Grid>
                    </Box>
                )}
                {/* Action Buttons */}
                <Box display="flex" gap={1} marginTop={1}>
                    <Button variant="contained" color="primary" startIcon={<Save />} onClick={onSave} disabled={isDrawingMode} size="small"> Save Changes </Button>
                    <Button variant="outlined" size="small" onClick={onCancel} disabled={isDrawingMode}> Cancel Edit </Button>
                </Box>
            </Box>
        </>
    );
});

export default DrawingEditor;
